import { useCallback, useEffect, useState } from "react"
import appInsightsLogger from "../../logging/appInsightsLogger"

type PollingFunction<T> = {
  apiServiceMethod: () => Promise<T>
}

export const usePollData = <T>(
  stopPollingCb: (responses: T[]) => boolean,
  handleErrorCb?: (err?: unknown) => void,
  handlePollingLimitReachedCb?: () => void,
) => {
  const [isPolling, setIsPolling] = useState(false)
  const [pollLimitReached, setPollLimitReached] = useState(false)
  const [pollingFunctions, setPollingFunctions] = useState<
    PollingFunction<T>[]
  >([])
  const [pollingResponse, setPollingResponse] = useState<T[]>([])

  const fetchPollingData = useCallback(async () => {
    try {
      const fetchData = async () => {
        const pollingFunctionsPromises = pollingFunctions.map((route) => {
          return route.apiServiceMethod()
        })
        const pollingData = await Promise.all(pollingFunctionsPromises)
        return pollingData
      }
      const data = await fetchData()

      return data
    } catch (error) {
      appInsightsLogger.traceError("usePollDataError", error)
      !!handleErrorCb && handleErrorCb(error)
    }
  }, [handleErrorCb, pollingFunctions])

  useEffect(
    () => {
      let pollingCount = 0
      let pollingInterval = 1000

      const handlePollingIteration = async () => {
        // Stop polling after 6 iterations
        if (pollingCount >= 6) {
          handlePollingLimitReachedCb?.()
          setPollLimitReached(true)
          setIsPolling(false)
          return
        }
        if (isPolling) {
          const pollingDataResponse = (await fetchPollingData()) || []
          pollingCount++
          setPollingResponse(pollingDataResponse)
          // Backoff
          pollingInterval = pollingInterval * 2
          const stopPolling = stopPollingCb(pollingDataResponse)
          if (stopPolling) {
            setIsPolling(false)
            return
          }
          // continue polling
          setTimeout(handlePollingIteration, pollingInterval)
        }
      }
      if (isPolling) {
        // Start polling
        setTimeout(handlePollingIteration, pollingInterval)
      }

      return () => {
        setIsPolling(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pollingFunctions, fetchPollingData],
  )

  const startPolling = useCallback((routes: PollingFunction<T>[]) => {
    setIsPolling(true)
    setPollingFunctions(routes)
  }, [])

  return { startPolling, isPolling, pollingResponse, pollLimitReached }
}
